/**
 * Offline submission queue (EDGE-14). A complaint composed without a
 * connection — files included, with the geotag captured at attachment
 * time — is kept in this browser's IndexedDB and submitted when the
 * connection returns. Nothing is sent anywhere until then.
 */
const DB = "seva-offline";
const STORE = "queue";

export interface QueuedSubmission {
  id?: number;
  url: string;
  payload: unknown;
  files: { name: string; type: string; blob: Blob }[];
  queuedAt: string;
}
export interface QueueResult {
  queuedAt: string;
  ok: boolean;
  reference?: string;
  trackingToken?: string;
  error?: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const r = fn(t.objectStore(STORE));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export const offlineQueueSupported = () => typeof indexedDB !== "undefined";
export const enqueue = (item: QueuedSubmission) => tx("readwrite", (s) => s.add(item));
export const listQueued = () => tx<QueuedSubmission[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedSubmission[]>);
const remove = (id: number) => tx("readwrite", (s) => s.delete(id));

let flushing = false;
/** Submits everything queued; returns one result per item attempted. */
export async function flushQueue(send: (item: QueuedSubmission) => Promise<any>): Promise<QueueResult[]> {
  if (flushing || !offlineQueueSupported() || !navigator.onLine) return [];
  flushing = true;
  const results: QueueResult[] = [];
  try {
    for (const item of await listQueued()) {
      try {
        const r = await send(item);
        results.push({ queuedAt: item.queuedAt, ok: true, reference: r.reference, trackingToken: r.trackingToken });
        await remove(item.id!);
      } catch (err: any) {
        if (err?.status === 0 || err?.code === "network_error") break; // still offline — keep it queued
        // A definite refusal (e.g. daily limit, validation) is reported and removed, never retried forever.
        results.push({ queuedAt: item.queuedAt, ok: false, error: err?.message ?? "Submission failed" });
        await remove(item.id!);
      }
    }
  } finally {
    flushing = false;
  }
  return results;
}
